import React, { useState, useCallback, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { API_BASE_URL } from '../config'

interface UploadItem {
  file: File
  id: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
  url?: string
}

interface ImageUploadProps {
  albumId: number
  onUploadSuccess?: () => void
}

// 并发上传数
const CONCURRENT_UPLOADS = 3

const ImageUpload: React.FC<ImageUploadProps> = ({ albumId, onUploadSuccess }) => {
  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [overallProgress, setOverallProgress] = useState(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [dragOverCount, setDragOverCount] = useState(0)

  // 上传单个文件
  const uploadFile = async (item: UploadItem, token: string): Promise<UploadItem> => {
    const formData = new FormData()
    formData.append('file', item.file)
    formData.append('albumId', albumId.toString())

    try {
      // 使用 XMLHttpRequest 以获得真实的上传进度
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100)
            setUploadQueue(prev =>
              prev.map(i => i.id === item.id ? { ...i, progress } : i)
            )
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error(`HTTP ${xhr.status}`))
          }
        })

        xhr.addEventListener('error', () => reject(new Error('网络错误')))
        xhr.addEventListener('abort', () => reject(new Error('已取消')))

        xhr.open('POST', `${API_BASE_URL}/api/upload`)
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        xhr.send(formData)
      })

      return { ...item, status: 'success', progress: 100 }
    } catch (err) {
      return { ...item, status: 'error', error: (err as Error).message }
    }
  }

  // 并发控制器
  const processQueue = async (items: UploadItem[]) => {
    setIsUploading(true)
    abortControllerRef.current = new AbortController()

    // 标记所有为 pending
    setUploadQueue(items.map(i => ({ ...i, status: 'pending' as const, progress: 0 })))

    const token = localStorage.getItem('token') || ''
    const results = [...items]
    let completed = 0

    // 使用并发池
    const executing = new Set<Promise<void>>()

    for (let i = 0; i < items.length; i++) {
      if (abortControllerRef.current?.signal.aborted) break

      // 标记开始上传
      setUploadQueue(prev =>
        prev.map(u => u.id === items[i].id ? { ...u, status: 'uploading' as const } : u)
      )

      const promise = uploadFile(items[i], token).then(result => {
        results[i] = result
        completed++
        setOverallProgress(Math.round((completed / items.length) * 100))
      })

      executing.add(promise)
      promise.then(() => executing.delete(promise))

      // 达到并发上限时等待一个完成
      if (executing.size >= CONCURRENT_UPLOADS) {
        await Promise.race(executing)
      }
    }

    // 等待剩余的完成
    await Promise.all(executing)

    setIsUploading(false)
    setUploadQueue(results)

    const successCount = results.filter(r => r.status === 'success').length
    const failCount = results.filter(r => r.status === 'error').length

    if (failCount > 0 && successCount > 0) {
      // 部分成功，保留队列让用户可以重试失败的
    } else {
      setTimeout(() => setUploadQueue([]), 3000)
    }

    onUploadSuccess?.()
  }

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return

    const items: UploadItem[] = acceptedFiles.map(file => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      status: 'pending' as const,
      progress: 0,
    }))

    await processQueue(items)
  }, [albumId, onUploadSuccess])

  // 重试失败的项目
  const retryFailed = async () => {
    const failedItems = uploadQueue.filter(i => i.status === 'error')
    if (failedItems.length > 0) {
      // 重置状态重新上传
      const resetItems = failedItems.map(i => ({ ...i, status: 'pending' as const, progress: 0, error: undefined }))
      await processQueue(resetItems)
    }
  }

  // 取消上传
  const cancelUpload = () => {
    abortControllerRef.current?.abort()
    setIsUploading(false)
    setUploadQueue(prev => prev.map(i =>
      i.status === 'uploading' || i.status === 'pending'
        ? { ...i, status: 'error', error: '已取消' }
        : i
    ))
  }

  // 清除队列
  const clearQueue = () => {
    setUploadQueue([])
    setOverallProgress(0)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp', '.svg']
    },
    multiple: true,
    disabled: isUploading,
  })

  const successCount = uploadQueue.filter(i => i.status === 'success').length
  const errorCount = uploadQueue.filter(i => i.status === 'error').length
  const totalCount = uploadQueue.length

  return (
    <div className="w-full">
      {/* 拖拽区域 */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
          ${isDragActive || dragOverCount > 0
            ? 'border-blue-500 bg-blue-50 scale-[1.01]'
            : isUploading
              ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50 bg-white'
          }
        `}
        onDragEnter={() => setDragOverCount(c => c + 1)}
        onDragLeave={() => setDragOverCount(c => Math.max(0, c - 1))}
        onDragEnd={() => setDragOverCount(0)}
      >
        <input {...getInputProps()} />

        <svg
          className={`mx-auto h-12 w-12 mb-4 transition-colors ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`}
          stroke="currentColor"
          fill="none"
          viewBox="0 0 48 48"
        >
          <path
            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>

        {isDragActive ? (
          <p className="text-blue-600 font-medium text-lg">释放文件以上传...</p>
        ) : isUploading ? (
          <p className="text-gray-500">正在上传中，请稍候...</p>
        ) : (
          <div>
            <p className="text-gray-700 font-medium mb-1">拖拽图片到此处，或点击选择</p>
            <p className="text-sm text-gray-400">支持 JPG / PNG / GIF / WebP / BMP · 可多选 · 支持批量上传</p>
          </div>
        )}
      </div>

      {/* 总体进度条 */}
      {(isUploading || totalCount > 0) && (
        <div className="mt-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* 头部：进度 + 操作 */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {isUploading && (
                <svg className="animate-spin h-4 w-4 text-blue-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              <span className="text-sm text-gray-700 truncate">
                {isUploading
                  ? `正在上传... ${overallProgress}% (${successCount}/${totalCount})`
                  : totalCount > 0
                    ? `上传完成 ✓ ${successCount} 成功${errorCount > 0 ? `，${errorCount} 失败` : ''}`
                    : ''
                }
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isUploading && errorCount > 0 && (
                <button
                  onClick={retryFailed}
                  className="px-3 py-1 text-xs font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                >
                  重试失败项
                </button>
              )}
              {isUploading && (
                <button
                  onClick={cancelUpload}
                  className="px-3 py-1 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  取消
                </button>
              )}
              {!isUploading && totalCount > 0 && (
                <button
                  onClick={clearQueue}
                  className="px-3 py-1 text-xs font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                >
                  清除
                </button>
              )}
            </div>
          </div>

          {/* 进度条 */}
          {isUploading && (
            <div className="h-1.5 bg-gray-100">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-300 ease-out rounded-r"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          )}

          {/* 文件列表 */}
          {uploadQueue.length > 0 && (
            <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
              {uploadQueue.map(item => (
                <div key={item.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                  {/* 状态图标 */}
                  <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                    {item.status === 'pending' && (
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                    )}
                    {item.status === 'uploading' && (
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {item.status === 'success' && (
                      <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {item.status === 'error' && (
                      <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>

                  {/* 文件名 */}
                  <span className={`text-sm truncate flex-1 min-w-0 ${
                    item.status === 'error' ? 'text-red-500 line-through' : 'text-gray-700'
                  }`}>
                    {item.file.name}
                    <span className="text-xs text-gray-400 ml-1.5">({formatSize(item.file.size)})</span>
                  </span>

                  {/* 进度/状态 */}
                  <div className="flex-shrink-0 text-xs text-gray-400 w-16 text-right">
                    {item.status === 'uploading' && `${item.progress}%`}
                    {item.status === 'success' && '✓ 完成'}
                    {item.status === 'error' && (item.error || '失败')}
                    {item.status === 'pending' && '排队中'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// 文件大小格式化
function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export default ImageUpload
